import { Descriptions, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '~/app/components/common/ConfirmDialog/ConfirmDialog';
import type { DisplayPlatformSetting, SettingValue } from '../../types';
import {
  deriveSettingTranslationKey,
  normalizeSettingSubmissionValue,
} from '../../utils';

export interface ConfirmSettingDialogProps {
  setting: DisplayPlatformSetting | null;
  draftValue?: SettingValue;
  reason: string;
  loading: boolean;
  onReasonChange: (reason: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ConfirmSettingDialog({
  setting,
  draftValue,
  reason,
  loading,
  onReasonChange,
  onCancel,
  onConfirm,
}: ConfirmSettingDialogProps) {
  const { t } = useTranslation('settings');
  const settingLabel = setting
    ? t(deriveSettingTranslationKey(setting.key, 'label'), {
        defaultValue: setting.key,
      })
    : '';
  const previewValue =
    setting?.valueType === 'CRON' && draftValue !== undefined
      ? normalizeSettingSubmissionValue(setting.valueType, draftValue)
      : draftValue;

  return (
    <ConfirmDialog
      open={Boolean(setting)}
      variant="warning"
      title={t('confirmation.title')}
      description={t('confirmation.description', { setting: settingLabel })}
      confirmLabel={t('confirmation.confirm')}
      cancelLabel={t('confirmation.cancel')}
      loading={loading}
      width={560}
      onCancel={onCancel}
      onConfirm={onConfirm}
    >
      {setting ? (
        <>
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label={t('confirmation.previousValue')}>
              {String(setting.value)}
            </Descriptions.Item>
            <Descriptions.Item label={t('confirmation.newValue')}>
              {String(previewValue)}
            </Descriptions.Item>
            <Descriptions.Item label={t('metadata.version')}>
              {setting.version}
            </Descriptions.Item>
          </Descriptions>
          <Input.TextArea
            value={reason}
            maxLength={500}
            showCount
            rows={3}
            placeholder={t('confirmation.reasonPlaceholder')}
            aria-label={t('confirmation.reasonLabel')}
            onChange={(event) => onReasonChange(event.target.value)}
          />
        </>
      ) : null}
    </ConfirmDialog>
  );
}
